/**
 * React hook for encounter-level chart replay control (time-based).
 * 
 * This is the replay "playhead controller" - manages the current replay position
 * and computes chart state at that position.
 * 
 * Purpose:
 * - Manage replay time position (playhead)
 * - Compute chart state at current replay time
 * - Provide API for scrubber, play/pause, audit review
 * 
 * Future integrations:
 * - Slider scrubber: bind replayTime to slider value, update chart as user drags
 * - Play/pause animation: increment replayTime automatically, pause at milestones
 * - Audit review: set replayTime to specific audit moment, review chart state
 * - Teaching mode: step through encounter showing chart progression
 * 
 * Note: This hook does NOT animate or render UI. It only manages state and
 * computes derived chart state. UI components consume this hook.
 */

import { useState, useMemo } from "react";
import type { ToothEvent } from "../../../types/domain";
import { replayEncounterChartAtTime } from "../../../utils/replayEncounterChartAtTime";

/**
 * Hook for encounter chart replay control.
 * 
 * @param events - Array of ToothEvent objects for the encounter (all teeth)
 * @returns Replay controller API
 */
export function useEncounterReplay(events: ToothEvent[]) {
  // Replay time position (null = live/current, string = specific ISO timestamp)
  const [replayTime, setReplayTime] = useState<string | null>(null);

  // Compute chart state at current replay time
  const chartState = useMemo(() => {
    if (!events || events.length === 0) {
      return {};
    }

    // If replayTime is null, get latest state (all events)
    // Otherwise, get state at specific time (events up to replayTime)
    return replayEncounterChartAtTime(events, replayTime || undefined);
  }, [events, replayTime]);

  // Reset replay to live/current state
  const resetReplay = () => {
    setReplayTime(null);
  };

  return {
    /** Current replay time position (null = live, string = ISO timestamp) */
    replayTime,
    /** Set replay time to specific ISO timestamp */
    setReplayTime,
    /** Reset replay to live/current state */
    resetReplay,
    /** Chart state at current replay time (toothCode → imageKey) */
    chartState,
  };
}
